// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Select } from 'components';
import {
  mountCollection,
  unmountCollection,
  requestChannelContent,
} from './ducks/channelContentActions';
import channelContentSaga from './ducks/channelContentSaga';
import {
  updateObservations,
  requestObservationsUpdate,
} from 'ducks/coreDataActions';
import ClientCard from 'containers/ClientCard';
import { CoreDataState, type CoreDatum } from 'ducks/coreDataReducer';
import type { AppState } from 'reducer';
import ContentRequest from 'pages/__components__/ContentRequest';
import { enterRoute, exitRoute } from 'ducks/routeActions';
import {
  EmptyCard,
  Button,
  CollectionItemTranisitionProvider,
} from 'components';
import HandleRequestError from 'containers/HandleRequestError';
import { Map } from 'immutable';
import { NoItemsMessage, RequestErrorMessage } from './EmptyChannelStates';
import {
  ChannelContentSortTypeOptions,
  ChannelContentSortDropdownOptions,
} from './channelContentSortUtils';
import {
  DefaultPaginationPageSize,
  calculatePaginationDisplay,
} from 'containers/search/PaginationUtils';

type OwnProps = {
  clientId: Id,
  channelName: string,
  apiMethod: string => void,
};

type StateProps = {
  requestStatus: RequestStatus,
  items: Array<CoreDatum>,
  role: UserRole,
  totalPages?: number,
};

type Actions = {
  enterRoute: enterRoute,
  exitRoute: exitRoute,
  mountCollection: mountCollection,
  unmountCollection: unmountCollection,
  onChange: updateObservations,
  onSubmit: requestObservationsUpdate,
  requestAction: () => requestChannelContent,
};

type Props = OwnProps & StateProps & { dispatch: Function };

type State = {
  currentPage: number,
  pageMarkers: Map<number, ?string>,
  pageCounts: Map<number, number>,
  contentSortType: Object,
};

export class Collection extends React.PureComponent<Props, State> {
  actions: Actions;

  constructor(props: Props) {
    super(props);
    const requestAction = (endDateTime, pageNumber) =>
      requestChannelContent(
        props.apiMethod,
        props.channelName,
        endDateTime,
        pageNumber
      );

    this.actions = bindActionCreators(
      {
        enterRoute,
        exitRoute,
        mountCollection,
        unmountCollection,
        onChange: updateObservations,
        onSubmit: requestObservationsUpdate,
        requestAction,
      },
      this.props.dispatch
    );
  }

  state = {
    currentPage: 1,
    pageMarkers: Map([[1, null]]),
    pageCounts: Map(),
    contentSortType: ChannelContentSortTypeOptions.SortByNewest,
  };

  componentDidMount() {
    const { channelName } = this.props;

    this.actions.mountCollection(channelName);
    this.actions.enterRoute({
      saga: channelContentSaga,
      sagaArgs: [channelName],
      cb: this.actions.requestAction,
    });
  }

  componentWillReceiveProps(nextProps: Props) {
    const { items: nextItems, requestStatus: nextRequestStatus } = nextProps;
    const { requestStatus } = this.props;

    if (requestStatus === 'pending' && nextRequestStatus === 'complete') {
      this.setState(prevState => {
        const { currentPage, pageCounts } = prevState;
        return {
          pageCounts: pageCounts.set(currentPage, nextItems.length),
        };
      });
    }
  }

  componentWillUnmount() {
    const { channelName } = this.props;

    this.actions.unmountCollection(channelName);
    this.actions.exitRoute();
  }

  changePage = (nextPage: number) => {
    const { items } = this.props;
    const { currentPage, pageMarkers } = this.state;
    let endDateTime;

    // when going back we should already have page marker
    if (currentPage > nextPage) {
      endDateTime = pageMarkers.get(nextPage);
    } else {
      endDateTime = items[items.length - 1].ugc.created_date;
    }

    this.setState({
      currentPage: nextPage,
      pageMarkers: pageMarkers.set(nextPage, endDateTime),
    });

    this.actions.requestAction(endDateTime, nextPage);
  };

  onSortOptionChange = (value: string) => {
    this.setState({
      contentSortType: ChannelContentSortTypeOptions[value],
    });
  };

  render() {
    const {
      items,
      requestStatus = null,
      role,
      clientId,
      totalPages,
    } = this.props;
    const { currentPage, pageCounts } = this.state;
    const currentPageCount = pageCounts.get(currentPage);
    const nextPageCount = pageCounts.get(currentPage + 1);
    const itemsSortedBySortType = this.state.contentSortType.sortFunction(
      items
    );

    return (
      <HandleRequestError
        requestStatus={requestStatus}
        match={err => typeof err.status === 'number' && err.status >= 400}
        render={RequestErrorMessage}
      >
        <ContentRequest
          requestStatus={requestStatus}
          render={() => {
            if (currentPage === 1 && currentPageCount === 0) {
              return NoItemsMessage();
            }

            if (currentPageCount === 0) {
              return (
                <EmptyCard>
                  <EmptyCard.Message>
                    <p>There are no more results</p>
                    <Button.Primary
                      onClick={() => this.changePage(currentPage - 1)}
                    >
                      Go back to previous page.
                    </Button.Primary>
                  </EmptyCard.Message>
                </EmptyCard>
              );
            }

            return (
              <React.Fragment>
                <div
                  style={{
                    margin: '-1em 0 -0.5em',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <PaginationStatus
                    currentPage={currentPage}
                    pageCounts={pageCounts}
                    totalPages={totalPages}
                  />
                  <div className="flex items-center">
                    <span className="b mr3 mid-gray">Sort by:</span>
                    <Select
                      options={ChannelContentSortDropdownOptions}
                      value={this.state.contentSortType}
                      clearable={false}
                      style={{ width: '200px' }}
                      onChange={(v: { value: string }) =>
                        this.onSortOptionChange(v.value)
                      }
                    />
                  </div>
                </div>
                <CollectionItemTranisitionProvider
                  items={itemsSortedBySortType}
                  itemKey={item => item.moderationState.getUGCId()}
                >
                  {item => (
                    <ClientCard
                      datum={item}
                      dispatch={this.props.dispatch}
                      role={role}
                      clientId={clientId}
                      onChange={this.actions.onChange}
                      onSubmit={this.actions.onSubmit}
                    />
                  )}
                </CollectionItemTranisitionProvider>
                <PaginationNav
                  currentPage={currentPage}
                  currentPageCount={currentPageCount}
                  nextPageCount={nextPageCount}
                  totalPages={totalPages}
                  onClick={this.changePage}
                />
              </React.Fragment>
            );
          }}
        />
      </HandleRequestError>
    );
  }
}

type PaginationNavProps = {
  currentPageCount: ?number,
  currentPage: number,
  nextPageCount: ?number,
  totalPages?: number,
  onClick: number => any,
};

function PaginationNav(props: PaginationNavProps) {
  const {
    currentPageCount,
    currentPage,
    nextPageCount,
    totalPages,
    onClick,
  } = props;

  if (typeof currentPageCount !== 'number') return null;

  // Check if API tells us there are more pages
  const hasMorePages =
    typeof totalPages === 'number' && totalPages > currentPage;

  // page navigation is not necessary in this case
  // BUT: if we have totalPages metadata (pwr-distro), show buttons even if < 100 items
  if (currentPageCount < 100 && currentPage === 1 && !hasMorePages) return null;
  // when we determine the next page is empty we skip rendering
  if (currentPage === 1 && nextPageCount === 0 && !hasMorePages) return null;

  return (
    <div className="flex justify-between">
      <Button
        className="ChannelContent_NavButton ChannelContent_NavButton--prev"
        onClick={() => onClick(currentPage - 1)}
        disabled={currentPage < 2}
      >
        Previous
      </Button>
      <Button
        className="ChannelContent_NavButton ChannelContent_NavButton--next"
        onClick={() => onClick(currentPage + 1)}
        disabled={
          !hasMorePages && (currentPageCount < 100 || nextPageCount === 0)
        }
      >
        Next
      </Button>
    </div>
  );
}

function PaginationStatus(props: {
  currentPage: number,
  pageCounts: Map<number, number>,
  totalPages?: number,
}) {
  const { currentPage, pageCounts, totalPages } = props;
  const currentPageCount = pageCounts.get(currentPage) || 0;
  const isFirstPage = currentPage === 1;

  if (isFirstPage && currentPageCount === 1) {
    return null;
  }

  // Use shared pagination logic when totalPages is available (pwr-distro)
  if (typeof totalPages === 'number') {
    const {
      first,
      last,
      total,
      potentiallyMoreItems,
    } = calculatePaginationDisplay({
      currentPage,
      currentPageCount,
      totalPages,
      getPageCount: pageNum => pageCounts.get(pageNum) || 0,
    });

    const content = `${first} - ${last} of ${total}${
      potentiallyMoreItems ? '+' : ''
    } items`;

    return (
      <span className="bg-silver white br-pill f6 pv1 ph2 PaginationStatus">
        {content}
      </span>
    );
  }

  // Legacy pagination display for non-distro endpoints
  let content;
  if (isFirstPage && currentPageCount < DefaultPaginationPageSize) {
    content = `${currentPageCount} items`;
  } else {
    const {
      first,
      last,
      total,
      potentiallyMoreItems,
    } = calculatePaginationDisplay({
      currentPage,
      currentPageCount,
      totalPages: undefined,
      getPageCount: pageNum => pageCounts.get(pageNum) || 0,
    });

    content = `${first} - ${last} of ${total}${
      potentiallyMoreItems ? '+' : ''
    } items`;
  }

  return (
    content && (
      <span className="bg-silver white br-pill f6 pv1 ph2 PaginationStatus">
        {content}
      </span>
    )
  );
}

const mapStateToProps = (state: AppState, ownProps: OwnProps): StateProps => {
  const { channelContent, coreData, auth } = state;
  const { channelName } = ownProps;

  const value = channelContent.get(channelName, 'pending');

  let requestStatus;
  let items = [];
  let totalPages;

  if (Array.isArray(value)) {
    // Legacy format: array of IDs
    requestStatus = 'complete';
    items = selectCoreData(coreData, value);
  } else if (
    value != null &&
    typeof value === 'object' &&
    !value.hasOwnProperty('status')
  ) {
    // New format: object with items and totalPages
    requestStatus = 'complete';
    items = selectCoreData(coreData, value.items || []);
    totalPages = value.totalPages;
  } else {
    // RequestStatus string
    requestStatus = value;
  }

  return {
    requestStatus,
    role: auth.role,
    items,
    totalPages,
  };
};

function selectCoreData(
  coreData: CoreDataState,
  ids: Array<Id>
): Array<CoreDatum> {
  return ids.reduce((acc, id) => {
    const datum = CoreDataState.getTheThings(coreData, id);

    if (datum != null) {
      acc.push(datum);
    }

    return acc;
  }, []);
}

export default connect(mapStateToProps)(Collection);



// WEBPACK FOOTER //
// ./src/containers/channels/ChannelContentCollection.js